const PBKDF2_ITERATIONS = 100000;
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const SALT_LENGTH = 16;

async function deriveKey(
  password: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  const baseKey = await crypto.subtle.importKey(
    "raw",
    passwordBuffer,
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: KEY_LENGTH },
    false,
    ["encrypt", "decrypt"]
  );
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export interface EncryptedData {
  encrypted: string;
  iv: string;
  salt: string;
}

export interface LabelConfig {
  name: string;
  color: number;
  webhook: 1 | 2;
}

export interface EncryptedConfig {
  url: string | null;
  auth: string | null;
  url_secondary: string | null;
  auth_secondary: string | null;
  voice_webhook_url: string | null;
  voice_enabled: boolean;
  voice_auto_send: boolean;
  admin_controls_features: boolean;
  qr_scanner_enabled: boolean;
  qr_auto_send: boolean;
  camera_enabled: boolean;
  gps_enabled: boolean;
  attachment_enabled: boolean;
  dark_mode: boolean;
  gradient_color: string | null;
  labels: LabelConfig[];
}

export interface N8nConfig {
  url: string;
  authPassword: string;
}

export async function encryptConfig(
  config: N8nConfig,
  password: string
): Promise<EncryptedData> {
  const encoder = new TextEncoder();
  const plaintext = encoder.encode(JSON.stringify(config));

  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const key = await deriveKey(password, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    plaintext
  );

  return {
    encrypted: arrayBufferToBase64(ciphertext),
    iv: arrayBufferToBase64(iv),
    salt: arrayBufferToBase64(salt),
  };
}

export async function encryptWebhookConfig(
  config: EncryptedConfig,
  masterKey: string
): Promise<EncryptedData> {
  const encoder = new TextEncoder();
  const plaintext = encoder.encode(JSON.stringify(config));

  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const key = await deriveKey(masterKey, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    plaintext
  );

  return {
    encrypted: arrayBufferToBase64(ciphertext),
    iv: arrayBufferToBase64(iv),
    salt: arrayBufferToBase64(salt),
  };
}

export async function decryptWebhookConfig(
  encryptedData: EncryptedData,
  masterKey: string
): Promise<EncryptedConfig> {
  const ciphertext = base64ToArrayBuffer(encryptedData.encrypted);
  const iv = base64ToArrayBuffer(encryptedData.iv);
  const salt = base64ToArrayBuffer(encryptedData.salt);

  const key = await deriveKey(masterKey, salt);

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ciphertext
  );

  const decoder = new TextDecoder();
  const json = decoder.decode(decrypted);

  return JSON.parse(json) as EncryptedConfig;
}

export async function decryptConfig(
  encryptedData: EncryptedData,
  password: string
): Promise<N8nConfig> {
  const ciphertext = base64ToArrayBuffer(encryptedData.encrypted);
  const iv = base64ToArrayBuffer(encryptedData.iv);
  const salt = base64ToArrayBuffer(encryptedData.salt);

  const key = await deriveKey(password, salt);

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ciphertext
  );

  const decoder = new TextDecoder();
  const json = decoder.decode(decrypted);

  return JSON.parse(json) as N8nConfig;
}

export async function testDecryption(
  encryptedData: EncryptedData,
  password: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await decryptConfig(encryptedData, password);
    return { success: true };
  } catch (error) {
    if (error instanceof DOMException && error.name === "OperationError") {
      return { success: false, error: "wrong_password" };
    }
    if (error instanceof SyntaxError) {
      return { success: false, error: "corrupted_data" };
    }
    return { success: false, error: "unknown_error" };
  }
}

// export async function testMasterKeyDecryption(
//   encryptedData: EncryptedData,
//   masterKey: string
// ): Promise<{ success: boolean; error?: string }> {
//   try {
//     await decryptWebhookConfig(encryptedData, masterKey);
//     return { success: true };
//   } catch (error) {
//     if (error instanceof DOMException && error.name === "OperationError") {
//       return { success: false, error: "wrong_password" };
//     }
//     if (error instanceof SyntaxError) {
//       return { success: false, error: "corrupted_data" };
//     }
//     return { success: false, error: "unknown_error" };
//   }
// }

export async function hashMasterKey(key: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(key);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function verifyMasterKey(key: string, storedHash: string): Promise<boolean> {
  const computedHash = await hashMasterKey(key);
  return computedHash === storedHash;
}

export function generateStrongPassword(length: number = 32): string {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
  const randomValues = crypto.getRandomValues(new Uint8Array(length));
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset[randomValues[i] % charset.length];
  }
  return password;
}

const TRANSPORT_IV_LENGTH = 12;

async function deriveTransportKey(transportSecret: string): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(transportSecret.padEnd(32, '0').slice(0, 32));

  return crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encryptForQRTransport(masterKey: string, transportSecret: string): Promise<string> {
  const encoder = new TextEncoder();
  const plaintext = encoder.encode(masterKey);

  const iv = crypto.getRandomValues(new Uint8Array(TRANSPORT_IV_LENGTH));
  const key = await deriveTransportKey(transportSecret);

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    plaintext
  );

  const combined = new Uint8Array(iv.length + new Uint8Array(ciphertext).length);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return arrayBufferToBase64(combined.buffer);
}

export async function decryptFromQRTransport(encryptedPayload: string, transportSecret: string): Promise<string> {
  const combined = base64ToArrayBuffer(encryptedPayload);

  const iv = combined.slice(0, TRANSPORT_IV_LENGTH);
  const ciphertext = combined.slice(TRANSPORT_IV_LENGTH);

  const key = await deriveTransportKey(transportSecret);

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ciphertext
  );

  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

export interface EncryptedPII {
  encrypted: string;
  iv: string;
}

export async function encryptPII(
  plaintext: string | null,
  masterKey: string,
  salt: Uint8Array
): Promise<EncryptedPII | null> {
  if (!plaintext) return null;

  const encoder = new TextEncoder();
  const data = encoder.encode(plaintext);

  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const key = await deriveKey(masterKey, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    data
  );

  return {
    encrypted: arrayBufferToBase64(ciphertext),
    iv: arrayBufferToBase64(iv),
  };
}

export async function decryptPII(
  encryptedData: EncryptedPII | null,
  masterKey: string,
  salt: Uint8Array
): Promise<string | null> {
  if (!encryptedData) return null;

  const ciphertext = base64ToArrayBuffer(encryptedData.encrypted);
  const iv = base64ToArrayBuffer(encryptedData.iv);

  const key = await deriveKey(masterKey, salt);

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ciphertext
  );

  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}
